import { APP_NAMES } from "@dwelldrop/shared";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

interface SidebarSection {
  key: string;
  label: string;
  alwaysOpen?: boolean;
  items: NavItem[];
}

const sidebarSections: SidebarSection[] = [
  {
    key: "overview",
    label: "Overview",
    alwaysOpen: true,
    items: [
      { to: "/", label: "Dashboard", icon: "📊" },
      { to: "/reports", label: "Performance", icon: "📈" },
    ],
  },
  {
    key: "user-management",
    label: "User Management",
    items: [
      { to: "/consumers", label: "Consumers", icon: "👥" },
      { to: "/venue-owners", label: "Venue Owners", icon: "🏠" },
      { to: "/admin-users", label: "Admin Users", icon: "🔑" },
      { to: "/property-operators", label: "Property Operators", icon: "🏢" },
    ],
  },
  {
    key: "venue-deals",
    label: "Venue & Deals",
    items: [
      { to: "/venues", label: "Venues", icon: "🏪" },
      { to: "/deals", label: "Deals", icon: "🎫" },
      { to: "/categories", label: "Categories", icon: "🏷️" },
      { to: "/legal-entities", label: "Legal Entities", icon: "🏛️" },
      { to: "/properties", label: "Properties", icon: "🏗️" },
      { to: "/transactions", label: "Transaction Detail", icon: "💰" },
      { to: "/invoices", label: "Invoices", icon: "🧾" },
      { to: "/popular-searches", label: "Popular Searches", icon: "🔍" },
      { to: "/qr-codes", label: "QR Codes", icon: "📲" },
    ],
  },
  {
    key: "points",
    label: "Points",
    items: [
      { to: "/points-ledger", label: "Points Ledger", icon: "📒" },
      { to: "/points-adjustments", label: "Admin Adjustments", icon: "✏️" },
      { to: "/points-reversals", label: "Reversal Requests", icon: "↩️" },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    items: [
      { to: "/alerts", label: "Alerts", icon: "🚨" },
      { to: "/monitoring", label: "Monitoring", icon: "📈" },
      { to: "/sla", label: "SLA Dashboard", icon: "🎯" },
      { to: "/error-rates", label: "Error Rates", icon: "📉" },
      { to: "/fraud", label: "Fraud Detection", icon: "🛡️" },
      { to: "/disputes", label: "Disputes", icon: "⚖️" },
      { to: "/shortlinks/tracking", label: "Shortlink Analytics", icon: "📊" },
    ],
  },
  {
    key: "compliance",
    label: "Compliance (PDPA)",
    items: [
      { to: "/data-requests", label: "Data Requests", icon: "📋" },
      { to: "/consent-management", label: "Consent Management", icon: "✅" },
      { to: "/retention-rules", label: "Retention Rules", icon: "🗄️" },
      { to: "/third-party", label: "Third-Party", icon: "🤝" },
      { to: "/policy-versions", label: "Policy Versions", icon: "📜" },
      { to: "/dpa-management", label: "DPA Management", icon: "📝" },
      // { to: "/breach-management", label: "Breach Management", icon: "🚨" },
      { to: "/dpo-settings", label: "DPO Settings", icon: "👤" },
      { to: "/audit-logs", label: "Audit Logs", icon: "📝" },
    ],
  },
  {
    key: "system",
    label: "System",
    items: [
      { to: "/global-config", label: "Global Config", icon: "🛠️" },
      { to: "/settings", label: "Settings", icon: "⚙️" },
    ],
  },
];

const STORAGE_KEY = "admin-sidebar-state";
const MOBILE_QUERY = "(max-width: 767px)";

function getActiveSectionKey(pathname: string): string | null {
  for (const section of sidebarSections) {
    for (const item of section.items) {
      if (item.to === "/") {
        if (pathname === "/") return section.key;
      } else if (pathname === item.to || pathname.startsWith(item.to + "/")) {
        return section.key;
      }
    }
  }
  return null;
}

export default function AdminLayout() {
  const { user, signOut } = useAdminAuth();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches,
  );

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    let stored: Record<string, boolean> = {};
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch {
      /* ignore */
    }

    const initial: Record<string, boolean> = {};
    const activeKey = getActiveSectionKey(window.location.pathname);
    for (const section of sidebarSections) {
      if (section.alwaysOpen) {
        initial[section.key] = true;
      } else {
        initial[section.key] = stored[section.key] ?? true;
        if (section.key === activeKey) initial[section.key] = true;
      }
    }
    return initial;
  });

  useEffect(() => {
    const activeKey = getActiveSectionKey(location.pathname);
    if (activeKey && !expandedSections[activeKey]) {
      setExpandedSections((prev) => {
        const next = { ...prev, [activeKey]: true };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }
  }, [location.pathname]);

  // Sidebar is open by default on desktop and closed on mobile; re-sync when
  // the viewport crosses the breakpoint.
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const handleChange = (e: MediaQueryListEvent): void => {
      setIsSidebarCollapsed(e.matches);
    };
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // Escape closes the mobile overlay drawer.
  useEffect(() => {
    if (isSidebarCollapsed) return;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && window.matchMedia(MOBILE_QUERY).matches) {
        setIsSidebarCollapsed(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isSidebarCollapsed]);

  // On mobile the sidebar overlays the page, so close it after navigating.
  useEffect(() => {
    if (window.matchMedia(MOBILE_QUERY).matches) {
      setIsSidebarCollapsed(true);
    }
  }, [location.pathname]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const hamburgerButton = (
    <button
      onClick={() => setIsSidebarCollapsed((prev) => !prev)}
      className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-50"
      aria-label="Toggle sidebar"
    >
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 12h16M4 18h16"
        />
      </svg>
    </button>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 supports-[height:100dvh]:h-dvh">
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Skip to content
      </a>

      {/* Mobile backdrop */}
      {!isSidebarCollapsed && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setIsSidebarCollapsed(true)}
          aria-hidden="true"
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar: overlay drawer on mobile, inline column from md up */}
      <aside
        className={`${isSidebarCollapsed ? "hidden" : "flex"} fixed inset-y-0 left-0 z-40 w-64 min-w-64 max-w-[85vw] flex-col border-r border-gray-200 bg-white shadow-xl md:static md:z-auto md:max-w-none md:shadow-none`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2 border-b border-gray-200 p-4">
          {hamburgerButton}
          <h1 className="text-lg font-bold text-gray-900">{APP_NAMES.admin}</h1>
        </div>

        <nav
          aria-label="Sidebar navigation"
          className="flex-1 space-y-3 overflow-y-auto py-3"
        >
          {sidebarSections.map((section) => {
            const isExpanded = expandedSections[section.key] ?? true;
            const panelId = `sidebar-section-${section.key}`;
            const buttonId = `sidebar-heading-${section.key}`;

            return (
              <div key={section.key} data-testid={`nav-section-${section.key}`}>
                {section.alwaysOpen ? (
                  <div className="space-y-1 px-[8px]">
                    {section.items.map(({ to, label, icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end
                        data-testid="nav-item"
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            isActive
                              ? "active bg-blue-50 text-blue-700"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          }`
                        }
                      >
                        <span
                          className="text-base"
                          aria-hidden="true"
                          data-testid="nav-icon"
                        >
                          {icon}
                        </span>
                        {label}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <>
                    <button
                      id={buttonId}
                      aria-expanded={isExpanded}
                      aria-controls={panelId}
                      onClick={() => toggleSection(section.key)}
                      data-testid={`nav-section-header-${section.key}`}
                      className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <span>{section.label}</span>
                      <span
                        className={`text-[10px] transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
                        aria-hidden="true"
                      >
                        ▶
                      </span>
                    </button>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      className={`overflow-hidden transition-[max-height] duration-200 ease-in-out ${
                        isExpanded ? "" : "max-h-0"
                      }`}
                    >
                      <div className="space-y-1 p-3">
                        {section.items.map(({ to, label, icon }) => (
                          <NavLink
                            key={to}
                            to={to}
                            end
                            data-testid="nav-item"
                            className={({ isActive }) =>
                              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                isActive
                                  ? "active bg-blue-50 text-blue-700"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                              }`
                            }
                          >
                            <span
                              className="text-base"
                              aria-hidden="true"
                              data-testid="nav-icon"
                            >
                              {icon}
                            </span>
                            {label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </nav>

        {/* User info + sign out */}
        <div
          className="border-t border-gray-200 p-3"
          data-testid="sidebar-footer"
        >
          <div
            className="truncate px-3 py-2 text-xs text-gray-500"
            data-testid="admin-email"
          >
            {user?.email}
          </div>
          <button
            onClick={signOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <span
              className="text-base"
              aria-hidden="true"
              data-testid="signout-icon"
            >
              🚪
            </span>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        id="main-content"
        className="flex min-w-0 flex-1 flex-col"
        tabIndex={-1}
      >
        {/* Header */}
        <header className="border-b border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-2">
            {isSidebarCollapsed && hamburgerButton}
            <h2 className="text-lg font-semibold text-gray-900">
              Admin Dashboard
            </h2>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
